<script lang="ts">
  import { onMount } from 'svelte';
  import { scale } from 'svelte/transition';
  import { Plus, Layers, DoorClosed, Lightbulb, Gamepad2, Cpu } from 'lucide-svelte';
  import { createZone, createRoom, createRcu, promptZoneForRoom } from '../../unitCreate';
  import type { EntityType } from '$lib/router';

  let open = $state(false);
  let rootEl: HTMLDivElement | undefined;

  type MenuItem = {
    type: EntityType;
    label: string;
    icon: typeof Layers;
    run: () => void | Promise<unknown>;
  };

  const items: MenuItem[] = [
    { type: 'zone', label: 'Zone', icon: Layers, run: () => createZone() },
    {
      type: 'room',
      label: 'Room',
      icon: DoorClosed,
      run: async () => {
        const zoneId = await promptZoneForRoom();
        if (zoneId != null) await createRoom(zoneId);
      }
    },
    {
      type: 'load',
      label: 'Load',
      icon: Lightbulb,
      run: () => document.dispatchEvent(new CustomEvent('open-create-load'))
    },
    {
      type: 'keypad',
      label: 'Device',
      icon: Gamepad2,
      run: () => document.dispatchEvent(new CustomEvent('open-create-device'))
    },
    { type: 'rcu', label: 'Hardware (RCU)', icon: Cpu, run: () => createRcu() }
  ];

  async function runItem(item: MenuItem) {
    open = false;
    await item.run();
  }

  async function runType(type: string) {
    const item = items.find((i) => i.type === type);
    if (item) await runItem(item);
  }

  function onWindowKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') open = false;
  }

  function onDocClick(e: MouseEvent) {
    if (open && rootEl && !rootEl.contains(e.target as Node)) open = false;
  }

  onMount(() => {
    function onOpenAdd(e: Event) {
      const type = (e as CustomEvent).detail?.type as string | undefined;
      if (type) runType(type);
      else open = true;
    }
    document.addEventListener('open-add-menu', onOpenAdd);
    document.addEventListener('click', onDocClick);
    window.addEventListener('keydown', onWindowKeydown);
    return () => {
      document.removeEventListener('open-add-menu', onOpenAdd);
      document.removeEventListener('click', onDocClick);
      window.removeEventListener('keydown', onWindowKeydown);
    };
  });
</script>

<!-- Floating add button, anchored to the bottom-right of the canvas (left of
     the 320px inspector), above the 24px status footer. -->
<div bind:this={rootEl} class="fixed z-40 bottom-10 right-[344px] flex flex-col items-end gap-2">
  {#if open}
    <div
      class="flex flex-col items-stretch min-w-[180px] rounded-lg border border-border-strong bg-bg-canvas shadow-toyama-lg py-1 overflow-hidden"
      transition:scale={{ duration: 130, start: 0.96, opacity: 0 }}
      role="menu"
      aria-label="Add to unit"
    >
      {#each items as item (item.type)}
        <button
          type="button"
          role="menuitem"
          class="flex items-center gap-2.5 px-3 py-2 text-sm text-text-secondary hover:bg-bg-hover hover:text-text-primary transition-colors duration-100 text-left"
          onclick={() => runItem(item)}
        >
          <svelte:component this={item.icon} class="w-4 h-4 shrink-0 text-text-muted" />
          {item.label}
        </button>
      {/each}
    </div>
  {/if}

  <button
    type="button"
    class="flex items-center justify-center h-11 w-11 rounded-full bg-accent-primary text-accent-on-primary shadow-toyama-lg hover:opacity-90 active:scale-95 transition-all duration-100"
    aria-label="Add to unit"
    aria-expanded={open}
    title="Add (Zone, Room, Load, Keypad, Hardware, Product)"
    onclick={() => (open = !open)}
  >
    <Plus class="w-5 h-5 transition-transform duration-150 {open ? 'rotate-45' : ''}" />
  </button>
</div>
